// frontend/src/components/SettingsComponent.js
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailIcon from '@mui/icons-material/Email';
import CloudIcon from '@mui/icons-material/Cloud';
import SecurityIcon from '@mui/icons-material/Security';
import SmsIcon from '@mui/icons-material/Sms';
import SystemUpdate from '../admin/SystemUpdate';


const SettingsComponent = () => {
  const [complaintAutoPendingTime, setComplaintAutoPendingTime] = useState('');
  const [fileUploadLimitMB, setFileUploadLimitMB] = useState(5);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [userRole, setUserRole] = useState(null);
  // Dev users (vendor allowlist, reported by GET /auth/me) get the System
  // Update tab. Server-side the /api/update routes re-check this — hiding
  // the tab here is only cosmetic.
  const [isDev, setIsDev] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  
  // Backup Configuration States
  const [backupConfig, setBackupConfig] = useState({
    email: {
      host: '',
      port: 587,
      secure: false,
      user: '',
      password: '',
      from: ''
    },
    googleDrive: {
      clientId: '',
      clientSecret: '',
      refreshToken: '',
      folderId: ''
    },
    awsS3: {
      accessKeyId: '',
      secretAccessKey: '',
      region: 'us-east-1',
      bucket: ''
    },
    defaultEncryption: false,
    defaultCompression: true,
    defaultValidation: true,
    backupRetentionDays: 30
  });

  // SMS Configuration States
  const [smsConfig, setSmsConfig] = useState({
    provider: 'jagratha',
    bulksms: {
      apiToken: '',
      sender: 'KCVENG',
      route: '2'
    },
    jagratha: {
      user: '',
      pass: '',
      sender: 'KCVENG',
      priority: 'ndnd',
      stype: 'normal'
    }
  });

  // Get user role from localStorage
  useEffect(() => {
    const role = localStorage.getItem('role');
    setUserRole(role);
    if (role === 'admin') {
      api
        .get('/auth/me', { skipErrorAlert: true })
        .then((resp) => setIsDev(!!resp.data.isDev))
        .catch(() => setIsDev(false));
    }
  }, []);

  // Fetch settings on component mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const role = localStorage.getItem('role');
        const requests = [
          api.get('/settings'),
          api.get('/settings/backup-config')
        ];
        
        // Only fetch SMS config for admins
        if (role === 'admin') {
          requests.push(api.get('/settings/sms-config'));
        }
        
        const responses = await Promise.all(requests);
        
        setComplaintAutoPendingTime(responses[0].data.complaintAutoPendingTime || 24);
        setFileUploadLimitMB(responses[0].data.fileUploadLimitMB || 5);
        setBackupConfig(responses[1].data);
        
        if (role === 'admin' && responses[2]) {
          setSmsConfig(responses[2].data);
        }
      } catch (error) {
        console.error('Error fetching settings:', error);
        if (error.response?.status === 403) {
          setMessage({ type: 'error', text: 'Access denied: You do not have permission to view settings.' });
        }
      } finally {
        setLoadingSettings(false);
      }
    };
    fetchSettings();
  }, []);

  const updateSettings = async () => {
    try {
      // Validate file upload limit
      if (fileUploadLimitMB < 1 || fileUploadLimitMB > 100) {
        setMessage({ type: 'error', text: 'File upload limit must be between 1 and 100 MB.' });
        return;
      }
      
      await api.put('/settings', { 
        complaintAutoPendingTime,
        fileUploadLimitMB 
      });
      setMessage({ type: 'success', text: 'Settings updated successfully.' });
    } catch (error) {
      console.error(error);
      if (error.response?.status === 403) {
        setMessage({ type: 'error', text: 'Access denied: You do not have permission to update settings.' });
      } else if (error.response?.data?.error) {
        setMessage({ type: 'error', text: error.response.data.error });
      } else {
        setMessage({ type: 'error', text: 'Error updating settings.' });
      }
    }
  };

  const updateBackupConfig = async () => {
    try {
      await api.put('/settings/backup-config', { backupConfig });
      setMessage({ type: 'success', text: 'Backup configuration updated successfully.' });
    } catch (error) {
      console.error(error);
      if (error.response?.status === 403) {
        setMessage({ type: 'error', text: 'Access denied: You do not have permission to update backup configuration.' });
      } else {
        setMessage({ type: 'error', text: 'Error updating backup configuration.' });
      }
    }
  };

  const changePassword = async () => {
    try {
      await api.post('/settings/change-password', { userId: 'currentUserId', oldPassword, newPassword });
      setMessage({ type: 'success', text: 'Password updated successfully.' });
      setOldPassword('');
      setNewPassword('');
    } catch (error) {
      console.error(error);
      setMessage({ type: 'error', text: 'Error changing password.' });
    }
  };

  const handleBackupConfigChange = (section, field, value) => {
    setBackupConfig(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const handleSmsConfigChange = (section, field, value) => {
    if (field === null) {
      // Direct property change (like provider)
      setSmsConfig(prev => ({
        ...prev,
        [section]: value
      }));
    } else {
      // Nested property change
      setSmsConfig(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
  };

  const updateSmsConfig = async () => {
    try {
      await api.put('/settings/sms-config', { smsConfig });
      setMessage({ type: 'success', text: 'SMS configuration updated successfully.' });
    } catch (error) {
      console.error(error);
      if (error.response?.status === 403) {
        setMessage({ type: 'error', text: 'Access denied: You do not have permission to update SMS configuration.' });
      } else {
        setMessage({ type: 'error', text: 'Error updating SMS configuration.' });
      }
    }
  };

  const isAdmin = userRole === 'admin';

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 2, p: 2 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
          <Typography variant="h5" gutterBottom>
            Settings
          </Typography>

          {/* Display Message */}
          {message.text && (
            <Box sx={{ mb: 2 }}>
              <Alert severity={message.type}>{message.text}</Alert>
            </Box>
          )}

          {/* Show a loader until settings are loaded */}
          {loadingSettings ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* Tabs for different settings sections */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange} variant="scrollable" allowScrollButtonsMobile>
                  <Tab label="General Settings" />
                  <Tab label="Password Change" />
                  {isAdmin && <Tab label="SMS Configuration" />}
                  {isAdmin && <Tab label="Backup Configuration" />}
                  {isAdmin && isDev && <Tab label="System Update" />}
                </Tabs>
              </Box>

              {/* General Settings Tab */}
              {activeTab === 0 && (
                <Box>
                  {isAdmin ? (
                    <Box>
                      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                        General Settings
                      </Typography>
                      
                      {/* Auto-Pending Time */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          Auto-Pending Time
                        </Typography>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              type="number"
                              label="Auto Pending Time (hours)"
                              value={complaintAutoPendingTime}
                              onChange={(e) => setComplaintAutoPendingTime(Number(e.target.value))}
                              fullWidth
                              helperText="Time in hours before complaints are automatically set to pending status"
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      
                      <Divider sx={{ my: 3 }} />
                      
                      {/* File Upload Limit */}
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle1" gutterBottom>
                          File Upload Limit
                        </Typography>
                        <Grid container alignItems="center" spacing={2}>
                          <Grid item xs={12} sm={8}>
                            <TextField
                              type="number"
                              label="Maximum File Size (MB)"
                              value={fileUploadLimitMB}
                              onChange={(e) => {
                                const value = Number(e.target.value);
                                if (value >= 1 && value <= 100) {
                                  setFileUploadLimitMB(value);
                                }
                              }}
                              fullWidth
                              inputProps={{ min: 1, max: 100, step: 1 }}
                              helperText="Maximum file size allowed for uploads (1-100 MB). This applies to both ID Proof and Complaint PDF files."
                            />
                          </Grid>
                        </Grid>
                      </Box>
                      
                      <Box sx={{ mt: 3 }}>
                        <Button variant="contained" color="primary" onClick={updateSettings} fullWidth>
                          Update Settings
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <Box sx={{ p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                      <Typography variant="body2" color="info.contrastText">
                        Note: Only administrators can modify system settings.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Password Change Tab */}
              {activeTab === 1 && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Change Password
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        type="password"
                        label="Old Password"
                        value={oldPassword}
                        onChange={(e) => setOldPassword(e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={5}>
                      <TextField
                        type="password"
                        label="New Password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                      <Button variant="contained" color="primary" onClick={changePassword} fullWidth>
                        Change
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* SMS Configuration Tab - Admin Only */}
              {activeTab === 2 && isAdmin && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    SMS Configuration
                  </Typography>
                  
                  {/* Provider Selection */}
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <SmsIcon sx={{ mr: 1 }} />
                      <Typography>SMS Provider Selection</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <FormControl fullWidth>
                            <InputLabel>Active SMS Provider</InputLabel>
                            <Select
                              value={smsConfig.provider || 'jagratha'}
                              label="Active SMS Provider"
                              onChange={(e) => handleSmsConfigChange('provider', null, e.target.value)}
                            >
                              <MenuItem value="jagratha">Jagratha SMS</MenuItem>
                              <MenuItem value="bulksms">BulkSMS</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Jagratha Configuration */}
                  <Accordion defaultExpanded={smsConfig.provider === 'jagratha'}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <SmsIcon sx={{ mr: 1 }} />
                      <Typography>Jagratha SMS Configuration</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Username"
                            value={smsConfig.jagratha?.user || ''}
                            onChange={(e) => handleSmsConfigChange('jagratha', 'user', e.target.value)}
                            fullWidth
                            helperText="Jagratha SMS API username"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="password"
                            label="Password"
                            value={smsConfig.jagratha?.pass || ''}
                            onChange={(e) => handleSmsConfigChange('jagratha', 'pass', e.target.value)}
                            fullWidth
                            helperText="Jagratha SMS API password"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Sender ID"
                            value={smsConfig.jagratha?.sender || 'KCVENG'}
                            onChange={(e) => handleSmsConfigChange('jagratha', 'sender', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth>
                            <InputLabel>Priority</InputLabel>
                            <Select
                              value={smsConfig.jagratha?.priority || 'ndnd'}
                              label="Priority"
                              onChange={(e) => handleSmsConfigChange('jagratha', 'priority', e.target.value)}
                            >
                              <MenuItem value="ndnd">NDND</MenuItem>
                              <MenuItem value="dnd">DND</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={3}>
                          <FormControl fullWidth>
                            <InputLabel>SMS Type</InputLabel>
                            <Select
                              value={smsConfig.jagratha?.stype || 'normal'}
                              label="SMS Type"
                              onChange={(e) => handleSmsConfigChange('jagratha', 'stype', e.target.value)}
                            >
                              <MenuItem value="normal">Normal</MenuItem>
                              <MenuItem value="flash">Flash</MenuItem>
                              <MenuItem value="unicode">Unicode</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* BulkSMS Configuration */}
                  <Accordion defaultExpanded={smsConfig.provider === 'bulksms'}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <SmsIcon sx={{ mr: 1 }} />
                      <Typography>BulkSMS Configuration</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            label="API Token"
                            value={smsConfig.bulksms?.apiToken || ''}
                            onChange={(e) => handleSmsConfigChange('bulksms', 'apiToken', e.target.value)}
                            fullWidth
                            helperText="BulkSMS API token"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Sender ID"
                            value={smsConfig.bulksms?.sender || 'KCVENG'}
                            onChange={(e) => handleSmsConfigChange('bulksms', 'sender', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth>
                            <InputLabel>Route</InputLabel>
                            <Select
                              value={smsConfig.bulksms?.route || '2'}
                              label="Route"
                              onChange={(e) => handleSmsConfigChange('bulksms', 'route', e.target.value)}
                            >
                              <MenuItem value="1">1 - Promotional</MenuItem>
                              <MenuItem value="2">2 - Transactional</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <Box sx={{ mt: 3 }}>
                    <Button variant="contained" color="primary" onClick={updateSmsConfig} fullWidth>
                      Update SMS Configuration
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Backup Configuration Tab - Admin Only */}
              {activeTab === 3 && isAdmin && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Backup Configuration
                  </Typography>
                  
                  {/* Default Settings */}
                  <Accordion defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <SecurityIcon sx={{ mr: 1 }} />
                      <Typography>Default Backup Settings</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={backupConfig.defaultEncryption}
                                onChange={(e) => handleBackupConfigChange('defaultEncryption', null, e.target.checked)}
                              />
                            }
                            label="Enable Default Encryption"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={backupConfig.defaultCompression}
                                onChange={(e) => handleBackupConfigChange('defaultCompression', null, e.target.checked)}
                              />
                            }
                            label="Enable Default Compression"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={backupConfig.defaultValidation}
                                onChange={(e) => handleBackupConfigChange('defaultValidation', null, e.target.checked)}
                              />
                            }
                            label="Enable Default Validation"
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="number"
                            label="Backup Retention (days)"
                            value={backupConfig.backupRetentionDays}
                            onChange={(e) => handleBackupConfigChange('backupRetentionDays', null, Number(e.target.value))}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Email Configuration */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <EmailIcon sx={{ mr: 1 }} />
                      <Typography>Email Configuration</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="SMTP Host"
                            value={backupConfig.email.host}
                            onChange={(e) => handleBackupConfigChange('email', 'host', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="number"
                            label="SMTP Port"
                            value={backupConfig.email.port}
                            onChange={(e) => handleBackupConfigChange('email', 'port', Number(e.target.value))}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Email User"
                            value={backupConfig.email.user}
                            onChange={(e) => handleBackupConfigChange('email', 'user', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="password"
                            label="Email Password"
                            value={backupConfig.email.password}
                            onChange={(e) => handleBackupConfigChange('email', 'password', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="From Email"
                            value={backupConfig.email.from}
                            onChange={(e) => handleBackupConfigChange('email', 'from', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={backupConfig.email.secure}
                                onChange={(e) => handleBackupConfigChange('email', 'secure', e.target.checked)}
                              />
                            }
                            label="Use Secure Connection"
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* Google Drive Configuration */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <CloudIcon sx={{ mr: 1 }} />
                      <Typography>Google Drive Configuration</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Client ID"
                            value={backupConfig.googleDrive.clientId}
                            onChange={(e) => handleBackupConfigChange('googleDrive', 'clientId', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="password"
                            label="Client Secret"
                            value={backupConfig.googleDrive.clientSecret}
                            onChange={(e) => handleBackupConfigChange('googleDrive', 'clientSecret', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="password"
                            label="Refresh Token"
                            value={backupConfig.googleDrive.refreshToken}
                            onChange={(e) => handleBackupConfigChange('googleDrive', 'refreshToken', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Folder ID"
                            value={backupConfig.googleDrive.folderId}
                            onChange={(e) => handleBackupConfigChange('googleDrive', 'folderId', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  {/* AWS S3 Configuration */}
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <CloudIcon sx={{ mr: 1 }} />
                      <Typography>AWS S3 Configuration</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Access Key ID"
                            value={backupConfig.awsS3.accessKeyId}
                            onChange={(e) => handleBackupConfigChange('awsS3', 'accessKeyId', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            type="password"
                            label="Secret Access Key"
                            value={backupConfig.awsS3.secretAccessKey}
                            onChange={(e) => handleBackupConfigChange('awsS3', 'secretAccessKey', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Region"
                            value={backupConfig.awsS3.region}
                            onChange={(e) => handleBackupConfigChange('awsS3', 'region', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            label="Bucket Name"
                            value={backupConfig.awsS3.bucket}
                            onChange={(e) => handleBackupConfigChange('awsS3', 'bucket', e.target.value)}
                            fullWidth
                          />
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <Box sx={{ mt: 3 }}>
                    <Button variant="contained" color="primary" onClick={updateBackupConfig} fullWidth>
                      Update Backup Configuration
                    </Button>
                  </Box>
                </Box>
              )}

              {/* System Update Tab - Dev Users Only */}
              {activeTab === 4 && isAdmin && isDev && <SystemUpdate />}
            </>
          )}
        </Paper>
      </Container>
  );
};

export default SettingsComponent;
